import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import DescriptionIcon from "@mui/icons-material/Description";
import PsychologyIcon from "@mui/icons-material/Psychology";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

const Navbar = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = !!localStorage.getItem("token");

  const handleLogoutConfirm = () => {
    localStorage.removeItem("token");
    setOpenLogoutDialog(false);
    navigate("/signup");
  };

  const handleMenuClick = (action) => {
    if (action === "account") navigate("/MyAccount");
    else if (action === "resume") navigate("/steps");
    else if (action === "ai-analyzer") navigate("/ai-analyzer");
    else if (action === "upload-resume") navigate("/resume-redesign");
    else if (action === "cgpa") navigate("/cgpa");
    else if (action === "logout") setOpenLogoutDialog(true);
    else if (action === "login") navigate("/signup");
    setOpenDrawer(false);
  };

  return (
    <>
      <header itemScope itemType="https://schema.org/WPHeader">
        <AppBar
          component="nav"
          aria-label="Main navigation"
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "white",
            color: "black",
            borderBottom: "1px solid #eee",
            top: 0,
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              minHeight: { xs: "54px", sm: "64px" },
              px: { xs: 1.5, sm: 2, md: 3 },
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                edge="start"
                disableRipple
                onClick={() => navigate("/")}
                aria-label="Go to homepage"
                sx={{ p: { xs: 0.5, sm: 1 } }}
              >
                <img
                  src={logo}
                  alt="Life Changers Ind Resume Builder logo"
                  width={isMobile ? 22 : 35}
                  height={isMobile ? 22 : 35}
                  style={{ width: isMobile ? 22 : 35, height: isMobile ? 22 : 35 }}
                />
              </IconButton>
              <Typography
                component="span"
                variant="h6"
                itemProp="name"
                sx={{
                  ml: 0.75,
                  fontWeight: "bold",
                  color: "purple",
                  cursor: "pointer",
                  fontSize: { xs: "0.92rem", sm: "1.1rem", md: "1.25rem" },
                  whiteSpace: "nowrap",
                }}
                onClick={() => navigate("/")}
                aria-label="Resume Builder home"
              >
                Resume Builder
              </Typography>
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box
                component="ul"
                role="list"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {/* 1️⃣ AI Analyzer */}
                <li>
                  <Button
                    startIcon={<PsychologyIcon sx={{ color: "#fff" }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: "1rem",
                      color: "#fff",
                      background: "linear-gradient(to right, #6a11cb, #2575fc)",
                      borderRadius: "25px",
                      px: 2,
                      py: 0.5,
                      boxShadow: "0 4px 15px rgba(106,17,203,0.35)",
                      border: "none",
                      "&:hover": {
                        background: "linear-gradient(to right, #2575fc, #6a11cb)",
                        boxShadow: "0 6px 20px rgba(106,17,203,0.5)",
                        transform: "scale(1.03)",
                      },
                    }}
                    onClick={() => handleMenuClick("ai-analyzer")}
                    aria-label="AI Resume Analyzer"
                  >
                    AI Analyzer
                  </Button>
                </li>

                {/* 1️⃣.5 Upload Resume (redesign) */}
                <li>
                  <Button
                    startIcon={<UploadFileIcon sx={{ color: "#002B5B" }} />}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "#002B5B",
                      borderRadius: "25px",
                      borderColor: "#002B5B",
                      px: 2,
                      py: 0.5,
                      "&:hover": {
                        background: "rgba(0,43,91,0.06)",
                        borderColor: "#002B5B",
                      },
                    }}
                    onClick={() => handleMenuClick("upload-resume")}
                    aria-label="Upload Resume"
                  >
                    Upload Resume
                  </Button>
                </li>

                {/* 1️⃣.7 CGPA Calculator */}
                <li>
                  <Button
                    startIcon={<CalculateIcon sx={{ color: "#2e7d32" }} />}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "#2e7d32",
                      borderRadius: "25px",
                      borderColor: "#2e7d32",
                      px: 2,
                      py: 0.5,
                      "&:hover": {
                        background: "rgba(46,125,50,0.06)",
                        borderColor: "#2e7d32",
                      },
                    }}
                    onClick={() => handleMenuClick("cgpa")}
                    aria-label="CGPA Calculator"
                  >
                    CGPA Calculator
                  </Button>
                </li>

                {/* 2️⃣ Build My Resume */}
                <li>
                  <Button
                    startIcon={<DescriptionIcon />}
                    variant="outlined"
                    aria-label="Build My Resume"
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      borderColor: "#2575fc",
                      color: "#2575fc",
                      fontWeight: "bold",
                      background: "linear-gradient(to right, #6a11cb, #2575fc)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      "&:hover": {
                        borderColor: "#6a11cb",
                        background: "linear-gradient(to right, #2575fc, #6a11cb)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      },
                    }}
                    onClick={() => handleMenuClick("resume")}
                  >
                    Build My Resume
                  </Button>
                </li>

                {/* 3️⃣ My Account */}
                {isLoggedIn && (
                  <li>
                    <Button
                      startIcon={<AccountCircleIcon />}
                      sx={{ color: "black", textTransform: "none", fontWeight: 500 }}
                      onClick={() => handleMenuClick("account")}
                      aria-label="My Account"
                    >
                      My Account
                    </Button>
                  </li>
                )}

                {/* 4️⃣ Login */}
                {!isLoggedIn && (
                  <li>
                    <Button
                      startIcon={<LoginIcon />}
                      sx={{ color: "black", textTransform: "none", fontWeight: 500 }}
                      onClick={() => handleMenuClick("login")}
                      aria-label="Login"
                    >
                      Login
                    </Button>
                  </li>
                )}

                {/* 4️⃣ Logout */}
                {isLoggedIn && (
                  <li>
                    <Button
                      startIcon={<LogoutIcon />}
                      sx={{ color: "black", textTransform: "none", fontWeight: 500 }}
                      onClick={() => handleMenuClick("logout")}
                      aria-label="Logout"
                    >
                      Logout
                    </Button>
                  </li>
                )}
              </Box>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton
                onClick={() => setOpenDrawer(true)}
                aria-label="Open navigation menu"
                sx={{ p: 1 }}
              >
                <MenuIcon sx={{ color: "black" }} />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      </header>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        aria-label="Mobile navigation drawer"
      >
        <Box sx={{ width: 240, pt: 2 }} role="navigation" aria-label="Mobile menu">
          <Box
            sx={{
              px: 2,
              pb: 2,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <img
              src={logo}
              alt=""
              aria-hidden="true"
              width={26}
              height={26}
            />
            <Typography fontWeight="bold" color="purple" fontSize="0.95rem">
              Resume Builder
            </Typography>
          </Box>

          <List sx={{ pt: 1 }}>
            {/* 1️⃣ AI Analyzer */}
            <ListItem
              button
              onClick={() => handleMenuClick("ai-analyzer")}
              sx={{ py: 1.5, backgroundColor: "#f0efff", borderRadius: "8px", mx: 1, mb: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PsychologyIcon sx={{ color: "#6c63ff" }} />
              </ListItemIcon>
              <ListItemText
                primary="AI Analyzer"
                primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 800, color: "#6c63ff" }}
              />
            </ListItem>

            {/* 1️⃣.5 Upload Resume (redesign) */}
            <ListItem
              button
              onClick={() => handleMenuClick("upload-resume")}
              sx={{ py: 1.5, backgroundColor: "#EFF7FF", borderRadius: "8px", mx: 1, mb: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <UploadFileIcon sx={{ color: "#002B5B" }} />
              </ListItemIcon>
              <ListItemText
                primary="Upload Resume"
                primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 700, color: "#002B5B" }}
              />
            </ListItem>

            {/* 1️⃣.7 CGPA Calculator */}
            <ListItem
              button
              onClick={() => handleMenuClick("cgpa")}
              sx={{ py: 1.5, backgroundColor: "#E8F5E9", borderRadius: "8px", mx: 1, mb: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CalculateIcon sx={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary="CGPA Calculator"
                primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 700, color: "#2e7d32" }}
              />
            </ListItem>

            {/* 2️⃣ Build My Resume */}
            <ListItem button onClick={() => handleMenuClick("resume")} sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DescriptionIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Build My Resume"
                primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 500 }}
              />
            </ListItem>

            {/* 3️⃣ My Account */}
            {isLoggedIn && (
              <ListItem button onClick={() => handleMenuClick("account")} sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountCircleIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="My Account"
                  primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 500 }}
                />
              </ListItem>
            )}

            {/* 4️⃣ Login */}
            {!isLoggedIn && (
              <ListItem button onClick={() => handleMenuClick("login")} sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LoginIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Login"
                  primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 500 }}
                />
              </ListItem>
            )}

            {/* 4️⃣ Logout */}
            {isLoggedIn && (
              <ListItem button onClick={() => handleMenuClick("logout")} sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 500 }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Logout Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout from your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogoutDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;